import dayjs from "dayjs";
import { adjustToTimezone, isSameMonth } from "./DateUtils";
import { format } from "date-fns";

export const isSelectableDay = (
  day: Date,
  currentMonth: Date,
  workingDays: Record<string, any>,
) => {
  // console.log("workingDays", workingDays);
  const today = adjustToTimezone(new Date());
  const isToday = dayjs(day).isSame(dayjs(today), "day");

  const currentMinutes = (today.getHours() + 4) * 60 + today.getMinutes();
  const currentWeekDay = format(day, "eeee").toLowerCase();
  const todaySchedule = workingDays?.[currentWeekDay];

  if (!todaySchedule?.working) return false;

  let fromMinutesToday = 0,
    toMinutesToday = 0;

  if (todaySchedule) {
    const [fromHours, fromMinutes] = todaySchedule.from.split(":").map(Number);
    const [toHours, toMinutes] = todaySchedule.to.split(":").map(Number);

    fromMinutesToday = fromHours * 60 + fromMinutes;
    toMinutesToday = toHours * 60 + toMinutes;

    if (toMinutesToday < fromMinutesToday) {
      toMinutesToday += 24 * 60;
    }
  }

  // Calculate 3 months from today
  const threeMonthsFromNow = new Date(today);
  threeMonthsFromNow.setMonth(today.getMonth() + 3);

  // Check if day is before or equal to 3 months limit
  const isWithinThreeMonths = day <= threeMonthsFromNow;

  const isSameDayOrLater =
    day.getFullYear() > today.getFullYear() ||
    (day.getFullYear() === today.getFullYear() &&
      day.getMonth() > today.getMonth()) ||
    (day.getFullYear() === today.getFullYear() &&
      day.getMonth() === today.getMonth() &&
      day.getDate() >= today.getDate());

  const isWorkingDay = workingDays[currentWeekDay]?.working;

  if (isToday && currentMinutes >= toMinutesToday) {
    return false;
  }

  return isSameDayOrLater && isWorkingDay && isWithinThreeMonths;
};

export const handleDateClick = (
  day: Date,
  selectedDate: Date | null,
  setSelectedDate: (date: Date | null) => void,
  currentMonth: Date,
  allDaysSelectable: boolean,
  workingDays: Record<string, any>,
) => {
  const isSelectable =
    allDaysSelectable || isSelectableDay(day, currentMonth, workingDays);

  if (!isSelectable) return;

  if (!selectedDate || day.toDateString() !== selectedDate.toDateString()) {
    setSelectedDate(day);
  }
};

export const handleRangeDateClick = (
  day: Date,
  selectedDateRange: { start: Date | null; end: Date | null } | undefined,
  setSelectedDateRange:
    | ((range: { start: Date | null; end: Date | null }) => void)
    | undefined,
  rangeStartSelected: boolean,
  setRangeStartSelected: (value: boolean) => void,
) => {
  if (!selectedDateRange || !setSelectedDateRange) return;

  if (!rangeStartSelected || !selectedDateRange.start) {
    // Select start date
    setSelectedDateRange({ start: day, end: null });
    setRangeStartSelected(true);
  } else if (
    rangeStartSelected &&
    selectedDateRange.start &&
    !selectedDateRange.end
  ) {
    // Select end date
    if (day > selectedDateRange.start) {
      setSelectedDateRange({ ...selectedDateRange, end: day });
      setRangeStartSelected(false);
    }
  } else {
    // Reset selection
    setSelectedDateRange({ start: day, end: null });
    setRangeStartSelected(true);
  }
};

export const getSelectionStatus = (
  day: Date,
  selectedDate?: Date | null,
  selectedDateRange?: { start: Date | null; end: Date | null },
) => {
  const isSelectedStart =
    selectedDateRange?.start &&
    day.toDateString() === selectedDateRange.start.toDateString();

  const isSelectedEnd =
    selectedDateRange?.end &&
    day.toDateString() === selectedDateRange.end?.toDateString();

  const isInSelectedRange =
    selectedDateRange?.start &&
    selectedDateRange?.end &&
    day > selectedDateRange.start &&
    day < selectedDateRange.end;

  const isSelectedSingle =
    selectedDate && day.toDateString() === selectedDate.toDateString();

  return {
    isSelectedStart,
    isSelectedEnd,
    isInSelectedRange,
    isSelectedSingle,
  };
};

export const getButtonClass = (
  day: Date,
  currentMonth: Date,
  selectable: boolean,
  isSelectedSingle: boolean,
  isSelectedStart: boolean,
  isSelectedEnd: boolean,
  isInSelectedRange: boolean,
) => {
  if (!isSameMonth(day, currentMonth) || !selectable) {
    return "!bg-gray-200 text-gray-500 text-opacity-30 cursor-none !cursor-not-allowed";
  } else if (isSelectedSingle || isSelectedStart || isSelectedEnd) {
    return "!bg-primary-500 text-white";
  } else if (isInSelectedRange) {
    return "!bg-primary-500 text-white";
  }
  return "bg-button-bg";
};
